import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * Destination-page hook that consumes the PQR Preview's focus-link
 * URL contract and brings the user's attention to the right field.
 *
 * Contract:
 *   - Reads `focusWeldId`, `focusField`, `focusPass` from the URL.
 *   - Waits for `ready` to flip true (typically: the page's own data
 *     finished loading, so the ref registry is populated).
 *   - Runs the optional `onBeforeFocus` callback once — useful for
 *     expanding collapsed accordions or panels so the target ref
 *     exists in the DOM.
 *   - Polls `resolveTarget` on a short interval until either it
 *     returns a non-null `HTMLElement` OR `missingTimeoutMs` elapses.
 *   - On success: `scrollIntoView({ block: 'center', behavior: 'smooth' })`,
 *     paints a `data-focus-flash="true"` attribute on the target for
 *     `flashDurationMs` (default 2500ms), and calls `.focus()`. Then
 *     strips ONLY the focus-* params from the URL (preserves
 *     `returnTo` and everything else).
 *   - On timeout: silently strips the focus-* params so a manual
 *     refresh does not keep re-attempting. No toast — navigation may
 *     still be correct even when a specific field marker is absent.
 *
 * Slice 01 ships the welder-row case (one params shape). Subsequent
 * slices reuse this hook with different `resolveTarget` implementations.
 */

const FOCUS_PARAM_KEYS = [
  'focusWeldId',
  'focusField',
  'focusPass',
  'focusPipe',
  'focusBevel'
] as const

export interface FocusParams {
  focusWeldId: string | null
  focusField: string | null
  focusPass: string | null
  /** Slice 05 — Materials per-row navigation. */
  focusPipe: string | null
  /** Slice 06 — Joint Preparation / Bevel Details navigation. */
  focusBevel: string | null
}

export interface UseFieldFocusHandlerOptions {
  /**
   * When `false`, the hook waits — the destination page has not yet
   * loaded the data needed to resolve the target ref.
   */
  ready: boolean
  /**
   * Resolver: given the current focus params, return the DOM element
   * to focus, or `null` if it's not yet present in the DOM.
   */
  resolveTarget: (params: FocusParams) => HTMLElement | null
  /**
   * Optional one-shot callback fired once before the first resolve
   * attempt. Use it to expand collapsed UI containers so the target
   * ref can be found on a subsequent tick.
   */
  onBeforeFocus?: (params: FocusParams) => void
  /** Default 2500ms. */
  flashDurationMs?: number
  /** Default 3000ms. */
  missingTimeoutMs?: number
}

function readFocusParams(search: URLSearchParams): FocusParams {
  return {
    focusWeldId: search.get('focusWeldId'),
    focusField: search.get('focusField'),
    focusPass: search.get('focusPass'),
    focusPipe: search.get('focusPipe'),
    focusBevel: search.get('focusBevel')
  }
}

function hasAnyFocusParam(params: FocusParams): boolean {
  return Boolean(
    params.focusWeldId ||
      params.focusField ||
      params.focusPass ||
      params.focusPipe ||
      params.focusBevel
  )
}

export function useFieldFocusHandler(opts: UseFieldFocusHandlerOptions): void {
  const [searchParams, setSearchParams] = useSearchParams()
  const handledKeyRef = useRef<string | null>(null)
  const beforeFocusFiredKeyRef = useRef<string | null>(null)

  const flashDurationMs = opts.flashDurationMs ?? 2500
  const missingTimeoutMs = opts.missingTimeoutMs ?? 3000

  // Key derived purely from the focus params so a fresh navigation with
  // the same params still triggers (e.g. user clicks the same field link
  // twice). When the user clears params we reset.
  const focusParams = readFocusParams(searchParams)
  const focusKey = hasAnyFocusParam(focusParams)
    ? [
        focusParams.focusWeldId ?? '',
        focusParams.focusField ?? '',
        focusParams.focusPass ?? '',
        focusParams.focusPipe ?? '',
        focusParams.focusBevel ?? ''
      ].join('|')
    : null

  useEffect(() => {
    if (!focusKey) {
      handledKeyRef.current = null
      beforeFocusFiredKeyRef.current = null
      return
    }
    if (!opts.ready) return
    if (handledKeyRef.current === focusKey) return

    if (
      opts.onBeforeFocus &&
      beforeFocusFiredKeyRef.current !== focusKey
    ) {
      beforeFocusFiredKeyRef.current = focusKey
      opts.onBeforeFocus(focusParams)
    }

    let cancelled = false
    let pollHandle: ReturnType<typeof setInterval> | null = null
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null

    const stripFocusParams = () => {
      const next = new URLSearchParams(searchParams)
      let mutated = false
      FOCUS_PARAM_KEYS.forEach((k) => {
        if (next.has(k)) {
          next.delete(k)
          mutated = true
        }
      })
      if (mutated) setSearchParams(next, { replace: true })
    }

    const succeed = (el: HTMLElement) => {
      handledKeyRef.current = focusKey
      try {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' })
      } catch {
        /* jsdom or unsupported — fine */
      }
      try {
        el.focus()
      } catch {
        /* not focusable — fine */
      }
      el.setAttribute('data-focus-flash', 'true')
      // Intentionally not gated on `cancelled`: the flash is a visual
      // affordance tied to the element, not to the effect lifecycle.
      // Stripping the URL params synchronously after this call would
      // otherwise cancel the cleanup and leave the attribute behind.
      setTimeout(() => {
        el.removeAttribute('data-focus-flash')
      }, flashDurationMs)
      stripFocusParams()
    }

    const tryResolve = () => {
      if (cancelled) return
      const target = opts.resolveTarget(focusParams)
      if (target) {
        if (pollHandle) clearInterval(pollHandle)
        if (timeoutHandle) clearTimeout(timeoutHandle)
        succeed(target)
      }
    }

    tryResolve()

    if (handledKeyRef.current !== focusKey) {
      pollHandle = setInterval(tryResolve, 100)
      timeoutHandle = setTimeout(() => {
        if (cancelled || handledKeyRef.current === focusKey) return
        if (pollHandle) clearInterval(pollHandle)
        handledKeyRef.current = focusKey
        stripFocusParams()
      }, missingTimeoutMs)
    }

    return () => {
      cancelled = true
      if (pollHandle) clearInterval(pollHandle)
      if (timeoutHandle) clearTimeout(timeoutHandle)
    }
    // We intentionally omit `opts` deps — `focusKey` and `opts.ready`
    // are the only meaningful triggers. Callbacks are read fresh each
    // invocation via closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey, opts.ready])
}
